/**
 * Query key factory for hotels-ui React Query hooks.
 * Follows the factory pattern for consistent cache management.
 *
 * Structure: ['hotels', namespace, ...params]
 * - Invalidating ['hotels'] clears all hotel-related caches
 * - Invalidating ['hotels', 'rooms'] clears all room queries
 * - Invalidating ['hotels', 'rooms', pbxId] clears rooms for a specific PBX
 */
export const HOTELS_QUERY_KEYS = {
  all: ['hotels'] as const,

  // Rooms
  rooms: () => [...HOTELS_QUERY_KEYS.all, 'rooms'] as const,
  roomsList: (pbxId: number) => [...HOTELS_QUERY_KEYS.rooms(), pbxId] as const,
  roomDetail: (extensionUuid: string) =>
    [...HOTELS_QUERY_KEYS.rooms(), 'detail', extensionUuid] as const,

  // Wake-up calls
  wakeupCalls: () => [...HOTELS_QUERY_KEYS.all, 'wakeupCalls'] as const,
  wakeupCallsList: (pbxUuid: string, params?: Record<string, unknown>) =>
    [...HOTELS_QUERY_KEYS.wakeupCalls(), pbxUuid, params] as const,
  roomWakeups: (
    pbxUuid: string,
    roomId: number,
    roomUuid: string,
    roomNumber: string,
    roomName: string,
    wakeupCallsCount: number | null,
  ) =>
    [
      ...HOTELS_QUERY_KEYS.wakeupCalls(),
      'room',
      pbxUuid,
      roomId,
      roomUuid,
      roomNumber,
      roomName,
      wakeupCallsCount ?? 0,
    ] as const,

  // Call history
  callHistory: () => [...HOTELS_QUERY_KEYS.all, 'callHistory'] as const,
  callHistoryList: (pbxId: number) =>
    [...HOTELS_QUERY_KEYS.callHistory(), pbxId] as const,
  emergencyCalls: (pbxId: number) =>
    [...HOTELS_QUERY_KEYS.callHistory(), 'emergency', pbxId] as const,

  // Voicemails
  voicemails: () => [...HOTELS_QUERY_KEYS.all, 'voicemails'] as const,
  voicemailsList: (pbxId: number, roomId: number) =>
    [...HOTELS_QUERY_KEYS.voicemails(), pbxId, roomId] as const,

  // PBX settings
  pbx: (pbxId: number) => [...HOTELS_QUERY_KEYS.all, 'pbx', pbxId] as const,

  // Extension lines (for settings page)
  extensionLines: () => [...HOTELS_QUERY_KEYS.all, 'extensionLines'] as const,
  extensionLinesList: (pbxId: number) =>
    [...HOTELS_QUERY_KEYS.extensionLines(), pbxId] as const,
} as const
